import { Logger } from '_common/services';
import { Command } from '../../Command';
import { JsonRange } from 'Editor/services/_Common/Selection';

export class ApplyDocumentStyleCommand extends Command {
  private documentStylesManager: Editor.Data.Styles.DocumentStylesManager | undefined;

  private styleToApply: string;
  private rangeToApply?: Editor.Selection.JsonRange;
  private options: Editor.Edition.StylesCommandOptions;

  constructor(
    context: Editor.Edition.Context,
    styleId: string,
    rangeToApply?: Editor.Selection.JsonRange,
    options: Editor.Edition.StylesCommandOptions = {},
  ) {
    super(context);
    this.styleToApply = styleId;
    this.rangeToApply = rangeToApply;
    this.options = {
      applySelection: false,
      createPatch: true,
      untrackedActionWarning: true,
      ...options,
    };

    this.documentStylesManager = this.context.DataManager?.styles.documentStyles;
  }

  private async applyDocumentStyleToRange(ctx: Editor.Edition.ActionContext) {
    if (
      !this.context.DataManager ||
      !this.context.contentManipulator ||
      !this.context.stylesManager
    ) {
      return;
    }

    const blocksData = JsonRange.filterElementDataFromRange(
      this.context.DataManager,
      ctx.range,
      ['p'],
      {
        sameContainerLevel: this.options.sameContainerLevel || false,
        useSelectedCells: this.options.useSelectedCells || true,
      },
    );

    for (let i = 0; i < blocksData.length; i++) {
      const baseData = blocksData[i].baseData;
      const blockData = blocksData[i].childData;
      const blockPath = blocksData[i].childPath;

      const baseModel = this.context.DataManager.nodes.getNodeModelById(baseData?.id || '');

      // check if element is editable
      if (!baseModel || !this.context.DataManager.nodes.isNodeEditable(baseModel.id)) {
        continue;
      }

      const range = new JsonRange(
        { b: baseModel.id, p: [...blockPath, 'childNodes', 0] },
        { b: baseModel.id, p: [...blockPath, 'childNodes', blockData.childNodes?.length || 0] },
      );

      // clear inline styles
      await this.context.stylesManager.clearFormat(range, {
        applySelection: false,
        untrackedActionWarning: false,
        createPatch: false,
        expandWord: false,
        resetParagraphStyle: false,
      });

      // TODO check if it is an outline
      if (blockData.id) {
        await this.context.DataManager?.numbering.includeNodeInOutline(blockData.id);
      }

      // apply style to block
      await this.context.contentManipulator.applyStyleToBlock(
        baseModel,
        blockPath,
        's',
        this.styleToApply,
      );
    }
  }

  protected async handleExec(...args: any[]): Promise<void> {
    if (this.context.debug) {
      Logger.trace('ApplyDocumentStyleCommand exec', this);
    }

    if (!this.documentStylesManager?.style(this.styleToApply)) {
      throw new Error('Style does not exist!');
    }

    if (this.options.untrackedActionWarning && !this.askUserAboutThis()) {
      return;
    }

    this.buildActionContext(this.rangeToApply);

    if (!this.actionContext) {
      throw new Error('Context is not defined');
    }

    await this.applyDocumentStyleToRange(this.actionContext);

    //trigger seelction check
    this.checkSelectionStatus();

    if (this.options.createPatch) {
      this.createPatch();
    }
  }
}
